/**
 * Sign-in. Functionally identical to the static portal's index.html/login.js:
 * password → Identity Platform → cookie; dev mode swaps the form to seeded
 * uids; forgot-password sends Google's own reset email with one neutral
 * outcome either way.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, loadConfig, sendPasswordReset, signIn, type AuthConfig } from '../api';

const MESSAGES: Record<string, string> = {
  no_account: 'That account is not set up in Tenpoint. Ask your administrator for access.',
  invite_pending: 'Your invitation has not been accepted yet. Check your email for the setup link.',
  account_disabled: 'This account has been disabled. Contact your administrator.',
  company_suspended: 'Your organization’s account is suspended. Contact support.',
  unauthenticated: 'Those credentials were not recognized.',
  forbidden: 'That account does not have access to the Tenpoint admin console.',
  session_expired: 'Your session has ended. Sign in again to continue.',
  server_error: 'Something went wrong on our side. Try again in a moment.',
  too_many_attempts: 'Too many failed attempts. Wait a few minutes and try again.',
  auth_not_configured: 'Sign-in is not configured for this environment. Contact support.',
};

const DEV_USERS = [
  { uid: 'dev-ryan', name: 'Ryan Strauss', role: 'Compliance lead', org: 'Lakeshore' },
  { uid: 'dev-anna', name: 'Anna Kowalski', role: 'Regional manager', org: 'Lakeshore' },
  { uid: 'dev-terrence', name: 'Terrence Bell', role: 'Site coordinator', org: 'Lakeshore' },
  { uid: 'dev-marcus', name: 'Marcus Reed', role: 'Compliance lead', org: 'Prairie' },
  { uid: 'dev-admin', name: 'Ryan Strauss', role: 'Platform admin', org: 'Tenpoint staff' },
];

export function Login() {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'signin' | 'reset' | 'reset-done'>('signin');
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const reason = params.get('reason');
    if (reason) setError(MESSAGES[reason] ?? 'Sign-in failed. Please try again.');
    loadConfig()
      .then(setConfig)
      .catch(() => setError('Cannot reach the Tenpoint API. Is the server running?'));
  }, [params]);

  async function submit(value: string, pass: string) {
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(value, pass);
      navigate(result.kind === 'admin' ? '/admin' : '/properties', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (MESSAGES[err.code] ?? 'Sign-in failed. Please try again.')
          : 'Sign-in failed. Please try again.',
      );
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return setError(MESSAGES.unauthenticated!);
    if (config && !config.devMode && !password) return setError(MESSAGES.unauthenticated!);
    void submit(email.trim(), password);
  }

  async function onReset(e: FormEvent) {
    e.preventDefault();
    if (!email.includes('@')) return setError('Enter the email address you sign in with.');
    setBusy(true);
    setError(null);
    try {
      await sendPasswordReset(email.trim());
      setMode('reset-done');
    } catch {
      setError('Could not send the reset email. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  const devMode = config?.devMode ?? false;

  return (
    <main className="login">
      <div className="login__box">
        <a className="login__brand" href="https://tenpointhq.com/" aria-label="Tenpoint">
          <span className="wordmark">
            <span className="wm-ten">Ten</span>
            <span className="wm-point">point</span>
          </span>
        </a>

        <div className="login__card">
          <h1>Sign in</h1>
          <p className="sub">Use your Tenpoint work account.</p>

          {error && <p className="login__error">{error}</p>}
          {mode === 'reset-done' && (
            <p className="login__reset-done">
              If that address has a Tenpoint account, a reset email is on its way. Check your inbox.
            </p>
          )}

          {mode === 'signin' && (
            <form onSubmit={onSubmit} noValidate>
              <div className="login__field">
                <label htmlFor="username">{devMode ? 'Developer user ID' : 'Email address'}</label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {!devMode && (
                <div className="login__field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              )}

              {!devMode && (
                <div className="login__row">
                  <span />
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setMode('reset');
                      setError(null);
                    }}
                  >
                    Forgot password?
                  </a>
                </div>
              )}

              <button className="login__submit" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={onReset} noValidate>
              <p className="sub" style={{ marginBottom: 16 }}>
                Enter your email and we’ll send a link to reset your password.
              </p>
              <div className="login__field">
                <label htmlFor="reset-email">Email address</label>
                <input
                  id="reset-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button className="login__submit" type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <p className="login__help" style={{ marginTop: 18 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setMode('signin');
                    setError(null);
                  }}
                >
                  Back to sign in
                </a>
              </p>
            </form>
          )}

          {devMode && mode === 'signin' && (
            <div className="login__dev">
              <p className="login__dev-title">Development sign-in</p>
              <p className="login__dev-body">No identity provider is configured, so sign in as a seeded user.</p>
              <div className="login__dev-users">
                {DEV_USERS.map((u) => (
                  <button key={u.uid} className="login__dev-user" onClick={() => void submit(u.uid, '')}>
                    <b>{u.name}</b>
                    <span>
                      {u.role} · {u.org}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <hr className="login__divider" />
          <p className="login__help">
            Trouble signing in? <a href="mailto:support@tenpointhq.com">Contact support</a>
          </p>
        </div>

        <p className="login__legal">Authorized users only. Access to tenant records is logged and monitored.</p>
      </div>
    </main>
  );
}
